import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import { canPerformAction, isModuleEnabled } from "../../utils/permissions";
import { ShieldExclamationIcon } from "@heroicons/react/24/outline";

/**
 * PagePermissionGuard component to protect entire pages based on user permissions
 *
 * @param {Object} props
 * @param {string} props.module - The module name to check permissions for
 * @param {string} props.action - The specific action to check (optional, defaults to 'read')
 * @param {React.ReactNode} props.children - The page content to render if permission is granted
 * @param {string} props.pageName - The name of the page for the access denied message
 * @param {boolean} props.requireModuleEnabled - Whether to require the module to be enabled (default: true)
 * @returns {React.ReactNode}
 */
const PagePermissionGuard = ({
  module,
  action = "read",
  children,
  pageName,
  requireModuleEnabled = true,
}) => {
  const { permissions } = useAuth();

  // If no module specified, always render children
  if (!module) {
    return children;
  }

  let hasPermission = false;

  if (action) {
    // Check specific action permission
    hasPermission = canPerformAction(permissions, module, action);
  } else if (requireModuleEnabled) {
    // Check if module is enabled
    hasPermission = isModuleEnabled(permissions, module);
  } else {
    // Just check if module exists in permissions
    hasPermission = permissions && permissions[module];
  }

  if (!hasPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <ShieldExclamationIcon className="w-8 h-8 text-red-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Access Denied
          </h1>

          {/* Description */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            You don't have permission to access {pageName || "this page"}.
            Please contact your administrator if you believe this is an error.
          </p>

          {/* Back to Dashboard Button */}
          <button
            onClick={() => window.history.back()}
            className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default PagePermissionGuard;
